import { useEffect, useState } from "react";
import { Menu, Layers, LayoutDashboard, Image as Img, Video, Settings, LogOut, Youtube, Home } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import AlertBell from "./AlertBell";
import { ThemeToggle } from "./theme-toggle";
import { logout } from "../api";

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(true);

  const nav = [
    { to: '/', label: 'Overview', icon: <LayoutDashboard size={18} /> },
    { to: '/projects', label: 'Projects', icon: <Video size={18} /> },
    { to: '/settings', label: 'Settings', icon: <Settings size={18} /> },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="h-screen flex">
        {/* Sidebar */}
        <aside className={`transition-all duration-300 ${open ? 'w-64' : 'w-16'} 
                         bg-white shadow-lg border-r border-slate-200
                         dark:bg-slate-900 dark:border-slate-800`}>
          {/* Logo Section */}
          <div className={`h-16 flex items-center ${open ? 'justify-between px-4' : 'justify-center px-2'} border-b border-slate-200 dark:border-slate-800`}>
            {open ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-slate-100">Vidmastro</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">AI Content Studio</div>
                  </div>
                </div>
                <button
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-center"
                  onClick={() => setOpen(o => !o)}
                  aria-label="Toggle sidebar"
                >
                  <Menu size={16} className="text-slate-600 dark:text-slate-400" />
                </button>
              </>
            ) : (
              <button
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-center w-full"
                onClick={() => setOpen(o => !o)}
                aria-label="Toggle sidebar"
              >
                <Menu size={16} className="text-slate-600 dark:text-slate-400" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-2">
            {nav.map(n => (
              <NavLink key={n.to} to={n.to}
                className={({ isActive }) => `flex items-center ${open ? 'gap-3 px-3' : 'justify-center px-2'} py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-800'
                  }`}>
                <span className="flex items-center justify-center">{n.icon}</span>
                <span className={`${open ? 'block' : 'hidden'} transition-opacity`}>{n.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Sidebar Footer */}
          {open && (
            <div className="absolute bottom-4 left-4 w-56">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-800 dark:to-slate-700 rounded-xl p-4 border border-blue-100 dark:border-slate-600">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-gray-900 dark:text-slate-100">System Status</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-slate-300">All systems operational</p>
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="h-16 bg-white shadow-sm border-b border-slate-200 dark:bg-slate-900 dark:border-slate-800 flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">VidMastro Studio</h1>
            </div>

            <div className="flex items-center gap-3">
              {/* User Profile */}
              <div className="flex items-center gap-3">
                <AlertBell />
                <ThemeToggle />

                {/* User Avatar */}
                <div className="flex items-center gap-3 pl-3 border-l border-slate-200 dark:border-slate-700">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">U</span>
                  </div>
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                    onClick={async () => {
                      try {
                        // Call logout API to clear session
                        await logout();

                        // Clear any local storage
                        localStorage.removeItem('vidmastro_userId');

                        // Redirect to login page
                        window.location.href = '/login';
                      } catch (error) {
                        console.error('Logout failed:', error);
                        // Fallback: force redirect to login
                        window.location.href = '/login';
                      }
                    }}
                    aria-label="Logout"
                  >
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100">Sign out</span>
                    <LogOut size={16} className="text-slate-500 dark:text-slate-400 group-hover:text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
